import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import AuthShell from '@/components/auth/AuthShell';
import type { LoginCredentials } from '@/types';
import { GOOGLE_SIGN_IN_URL, challengeFromHash, googleSignInEnabled, type TwoStepChallenge } from '@/api/auth';
import TwoStepCodeForm from '@/components/auth/TwoStepCodeForm';
import { googleErrorMessage, launchErrorMessage } from '@/lib/launchErrors';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

export default function LoginPage() {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  // Two-step sign-in on: the password passed and the account now needs its code.
  const [challenge, setChallenge] = useState<TwoStepChallenge | null>(null);
  // Sent here by the MyStateMLS button when it could not sign the member in.
  const [params] = useSearchParams();
  const launchError = launchErrorMessage(params.get('launch_error')) ?? googleErrorMessage(params.get('google_error'));
  const { data: googleOn } = useQuery({ queryKey: ['google-sign-in'], queryFn: googleSignInEnabled, staleTime: Infinity });
  // Back from Google on a two-step account: go straight to the code step, and drop the
  // challenge from the address bar.
  useEffect(() => {
    const fromGoogle = challengeFromHash(window.location.hash);
    if (fromGoogle) {
      setChallenge(fromGoogle);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: LoginCredentials) => {
    setServerError('');
    try {
      const next = await login(data);
      if (next) setChallenge(next);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Login failed. Please check your credentials.';
      setServerError(message);
    }
  };

  if (challenge) {
    return (
      <AuthShell
        title="Enter your code"
        subtitle="Two-step sign-in is on for this account."
      >
        <TwoStepCodeForm challenge={challenge} onBack={() => setChallenge(null)} />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to manage your site and leads."
      altPrompt="Don't have an account?"
      altCta="Create one free"
      altTo="/register"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {launchError && !serverError && (
          <div role="status" className="flex items-start gap-2.5 rounded-lg border border-brand-border bg-brand-lilac-soft px-3.5 py-2.5 text-sm text-brand-ink">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-plum" />
            <span>{launchError}</span>
          </div>
        )}
        {serverError && (
          <div className="flex items-start gap-2.5 rounded-lg border border-brand-error/30 bg-brand-error-soft px-3.5 py-2.5 text-sm text-brand-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{serverError}</span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
          />
          {errors.email && (
            <p className="text-xs text-brand-error">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/password-reset" className="text-xs font-medium text-brand-plum hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              {...register('password')}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ink-soft transition-colors hover:text-brand-plum"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-brand-error">{errors.password.message}</p>
          )}
        </div>

        <Button type="submit" className="h-11 w-full text-sm" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>

      {googleOn && (
        <div className="mt-5 space-y-5">
          <div className="flex items-center gap-3 text-xs text-brand-ink-soft">
            <span className="h-px flex-1 bg-brand-border" />
            or
            <span className="h-px flex-1 bg-brand-border" />
          </div>
          <a
            href={GOOGLE_SIGN_IN_URL}
            className="flex h-11 w-full items-center justify-center gap-2.5 rounded-lg border border-brand-border bg-white text-sm font-semibold text-brand-midnight transition-colors hover:bg-brand-lilac-soft"
          >
            <GoogleMark />
            Continue with Google
          </a>
        </div>
      )}
    </AuthShell>
  );
}

/** Google's "G" (brand colours are Google's, by their sign-in branding rules). */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="h-4 w-4">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
